import React, { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { authService } from '../services/authService';
import { loginSuccess } from '../store/slices/authSlice';
import { LoginRequest } from '../types';

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastEmail, setLastEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const isVerifyError = !!errorMessage?.toLowerCase().includes('verify');

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<LoginRequest>();

  const onSubmit = async (data: LoginRequest) => {
    // Prevent double submission
    if (loading) return;

    try {
      setLoading(true);
      // Don't reset error here - let it stay visible until success
      const response = await authService.login(data);
      // Only clear error on successful login
      setLoginError(false);
      dispatch(loginSuccess(response));
      toast.success(t('auth.loginSuccess'));
      navigate('/app/dashboard');
    } catch (error: any) {
      setLoginError(true);
      setLastEmail(data.email);
      // Get error message from backend response
      const message = error.response?.data?.message;
      setErrorMessage(message || null);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const email = getValues('email') || lastEmail;
    if (!email) {
      toast.error(t('auth.enterEmailFirst'));
      return;
    }
    try {
      setSendingVerification(true);
      await authService.resendVerification(email);
      toast.success('Doğrulama e-postası gönderildi. Lütfen gelen kutunuzu kontrol edin.');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Doğrulama e-postası gönderilemedi.');
    } finally {
      setSendingVerification(false);
    }
  };

  const handleSendResetEmail = async () => {
    const email = getValues('email') || lastEmail;
    if (!email) {
      toast.error(t('auth.enterEmailFirst'));
      return;
    }

    try {
      setSendingReset(true);
      await authService.forgotPassword(email);
      toast.success(t('auth.resetEmailSent'));
      setLoginError(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('auth.resetEmailFailed'));
    } finally {
      setSendingReset(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 sm:p-8">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Transfer Paneli</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-2">{t('auth.signInToContinue')}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.emailAddress')}
            </label>
            <input
              type="email"
              {...register('email', {
                required: t('auth.emailRequired'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('auth.emailInvalid'),
                },
              })}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px]"
              placeholder={t('auth.enterEmail')}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.password')}
            </label>
            <input
              type="password"
              {...register('password', {
                required: t('auth.passwordRequired'),
                minLength: {
                  value: 8,
                  message: t('auth.passwordMinLength'),
                },
              })}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px]"
              placeholder={t('auth.enterPassword')}
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
            )}
          </div>

          {/* Login Error with Reset Password Option - Always rendered, visibility controlled by CSS */}
          <div
            className={`bg-red-50 border border-red-200 rounded-lg p-4 transition-all duration-200 ${
              loginError ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0 overflow-hidden p-0 border-0'
            }`}
            style={{ display: loginError ? 'block' : 'none' }}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  {t('auth.loginFailed')}
                </p>
                <p className="text-sm text-red-600 mt-1">
                  {isVerifyError
                    ? 'Giriş yapmadan önce e-posta adresinizi doğrulamanız gerekmektedir.'
                    : (errorMessage || t('auth.wrongCredentials'))}
                </p>
                {isVerifyError && (
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={sendingVerification}
                      className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {sendingVerification ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Doğrulama E-postasını Yeniden Gönder
                        </>
                      )}
                    </button>
                  </div>
                )}
                {!errorMessage?.includes('deactivated') && !isVerifyError && (
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={handleSendResetEmail}
                    disabled={sendingReset}
                    className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {sendingReset ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('auth.sending')}
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {t('auth.sendResetEmail')}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoginError(false)}
                    className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    {t('auth.tryAgain')}
                  </button>
                </div>
                )}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 active:bg-blue-800 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium min-h-[44px] transition-colors"
          >
            {loading ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">{t('auth.orDivider')}</span>
          </div>
        </div>

        {/* Google Login */}
        <div className="flex justify-center">
          {googleLoading ? (
            <div className="text-sm text-gray-500">{t('common.loading')}</div>
          ) : (
            <GoogleLogin
              onSuccess={async (credentialResponse) => {
                if (!credentialResponse.credential) return;
                setGoogleLoading(true);
                try {
                  const response = await authService.googleAuth(credentialResponse.credential);
                  dispatch(loginSuccess(response));
                  toast.success(t('auth.loginSuccess'));
                  navigate('/app/dashboard');
                } catch (error: any) {
                  const message = error.response?.data?.message || t('auth.googleFailed');
                  toast.error(message);
                } finally {
                  setGoogleLoading(false);
                }
              }}
              onError={() => {
                toast.error(t('auth.googleFailed'));
              }}
              width="100%"
              text="signin_with"
              shape="rectangular"
              size="large"
            />
          )}
        </div>

        {/* Forgot Password Link */}
        <div className="mt-4 text-center">
          <Link
            to="/forgot-password"
            className="text-sm text-gray-600 hover:text-blue-600 hover:underline"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/register"
            className="text-sm text-blue-600 hover:underline"
          >
            {t('auth.noAccount')} {t('auth.register')}
          </Link>
        </div>
      </div>
    </div>
  );
};
